import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilies } from '../hooks/useFamilies';
import { useDebouncedValue } from '../hooks/useDebounce';
import { formatDate } from '../utils/dateUtils';
import '../styles/FamilyListPage.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const FamilyListPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const debouncedSearch = useDebouncedValue(searchTerm, 300);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  // Server-side search + pagination
  const { data, isLoading, isFetching, error } = useFamilies({
    page,
    pageSize,
    searchQuery: debouncedSearch || undefined,
  });

  const families = data?.data || [];
  const serverPagination = data?.pagination;
  const totalItems = serverPagination?.totalItems ?? families.length;
  const totalPages = serverPagination?.totalPages ?? Math.max(1, Math.ceil(families.length / pageSize));

  // Gate the "no families found" empty state while a search is still in
  // flight (loading, background-fetching, or the debounce hasn't caught up
  // yet) so we never flash "not found" for a query that simply hasn't
  // resolved. Mirrors the same fix applied to DonorListPage.
  const isSearching = isLoading || isFetching || debouncedSearch !== searchTerm;

  const handleCreateFamily = () => {
    navigate('/families/create');
  };

  const handleViewFamily = (familyID: number) => {
    navigate(`/families/${familyID}`);
  };

  if (isLoading) {
    return (
      <div className="family-list-page">
        <div className="loading">Loading families...</div>
      </div>
    );
  }

  return (
    <div className="family-list-page">
      <div className="page-header">
        <div>
          <h1>Families</h1>
          <p className="subtitle">Manage family groups for grant reporting</p>
        </div>
        <button className="btn-primary" onClick={() => navigate('/families/create')}>
          + Create Family
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          {error instanceof Error ? error.message : 'Failed to load families'}
        </div>
      )}

      <div className="search-bar">
        <input
          type="text"
          placeholder="Search families by name, contact, or ID..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="search-input"
        />
        {debouncedSearch !== searchTerm && searchTerm && (
          <small style={{ color: '#666', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
            Searching...
          </small>
        )}
      </div>

      <div className="results-count">
        Showing {families.length} of {totalItems} families
        {totalPages > 1 && ` (Page ${page} of ${totalPages})`}
      </div>

      <div className="families-grid">
        {families.length === 0 && isSearching ? (
          <div className="empty-state">
            <p>Searching…</p>
          </div>
        ) : families.length === 0 ? (
          <div className="empty-state">
            {searchTerm ? (
              <p>No families found matching &quot;{searchTerm}&quot;</p>
            ) : (
              <div>
                <p>No families created yet</p>
                <button className="btn-secondary" onClick={handleCreateFamily}>
                  Create your first family
                </button>
              </div>
            )}
          </div>
        ) : (
          families.map(family => (
            <div
              key={family.familyID}
              className="family-card"
              onClick={() => handleViewFamily(family.familyID!)}
            >
              <div className="family-card-header">
                <h3>{family.familyName}</h3>
                <span className="member-count">{family.memberCount} member{family.memberCount !== 1 ? 's' : ''}</span>
              </div>
              <div className="family-card-body">
                <div className="info-row">
                  <span className="label">Primary Contact:</span>
                  <span className="value">{family.primaryContactName}</span>
                </div>
                <div className="info-row">
                  <span className="label">Client ID:</span>
                  <span className="value">{family.primaryContactClientID}</span>
                </div>
                {family.createdAt && (
                  <div className="info-row">
                    <span className="label">Created:</span>
                    <span className="value">{formatDate(family.createdAt)}</span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {families.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '1rem',
          marginTop: '1.5rem',
          padding: '1rem',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label htmlFor="pageSize" style={{ fontSize: '0.9rem', color: '#666' }}>
              Show:
            </label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              style={{
                padding: '0.4rem 0.6rem',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontSize: '0.9rem'
              }}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>

          {totalPages > 1 && (
            <>
              <button
                className="btn-secondary"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || isLoading}
              >
                Previous
              </button>

              <span style={{ fontSize: '0.9rem', color: '#666' }}>
                Page {page} of {totalPages}
              </span>

              <button
                className="btn-secondary"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || isLoading}
              >
                Next
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default FamilyListPage;
